import type { HeadersFunction, LoaderFunctionArgs, MetaFunction } from 'react-router'
import { Link, useLoaderData } from 'react-router'

import social from '@/assets/social.png?url'
import Paginator from '@/components/Paginator'
import SiteFooter from '@/components/SiteFooter'
import SiteHeader from '@/components/SiteHeader'
import { findPaginatedChangelogs } from '@/models/changelog.server'
import { pageHelper } from '@/request'
import { formatLong } from '@/utils/date'

type LoaderData = typeof loader

export async function loader({ url }: LoaderFunctionArgs) {
  const page = pageHelper(url)

  const { changelogs, pagination } = await findPaginatedChangelogs({ page: page, perPage: 25 })
  const { root } = page

  return {
    changelogs: changelogs,
    pagination: pagination,
    root: root
  }
}

export const headers: HeadersFunction = ({ parentHeaders }) => ({
  'Cache-Control': parentHeaders.get('Cache-Control') ?? ''
})

export const meta: MetaFunction<LoaderData> = ({ loaderData }) => {
  if (!loaderData) return []

  const { root } = loaderData

  const title = 'Changelog — PlanetScale'
  const description = 'The latest PlanetScale features and product launches.'
  const image = `${root}${social}`

  return [
    { title },
    { name: 'description', content: description },
    { property: 'og:url', content: 'https://planetscale.com/changelog' },
    { property: 'og:type', content: 'website' },
    { property: 'og:title', content: title },
    { property: 'og:image', content: image },
    { property: 'og:description', content: description },
    { property: 'twitter:card', content: 'summary_large_image' },
    { property: 'twitter:site', content: '@PlanetScale' },
    { property: 'twitter:creator', content: '@PlanetScale' },
    { property: 'twitter:url', content: 'https://planetscale.com/changelog' },
    { property: 'twitter:title', content: title },
    { property: 'twitter:description', content: description },
    { property: 'twitter:image', content: image },
    { tagName: 'link', rel: 'canonical', href: 'https://planetscale.com/changelog' }
  ]
}

export default function ChangelogIndex() {
  const { changelogs, pagination } = useLoaderData<LoaderData>()

  return (
    <>
      <SiteHeader />

      <main className='container mb-6 max-w-7xl px-3 sm:px-5 lg:px-12'>
        <section>
          <h1>Changelog</h1>

          <p>
            The latest PlanetScale features and product launches. Get the <a href='/changelog/feed.atom'>RSS feed</a>.
          </p>

          {changelogs.map((changelog) => (
            <div key={changelog.slug}>
              <hr className='dot-fill' />

              <h2>
                <Link
                  className='hover:bg-gray-900 hover:text-white hover:no-underline dark:hover:bg-gray-50 dark:hover:text-black'
                  prefetch='intent'
                  to={`/changelog/${changelog.slug}`}
                >
                  {changelog.title}
                </Link>
              </h2>

              <p className='text-secondary'>
                <time dateTime={changelog.createdAt}>{formatLong(changelog.createdAt)}</time>
              </p>

              <p>
                {changelog.excerpt}{' '}
                <Link
                  className='text-default bg-blue-100 px-sm hover:bg-blue-600 hover:text-white dark:bg-blue-900 dark:text-blue dark:hover:bg-blue-500 dark:hover:text-black'
                  prefetch='intent'
                  to={`/changelog/${changelog.slug}`}
                >
                  →
                </Link>
              </p>
            </div>
          ))}

          <Paginator pagination={pagination} />
        </section>
      </main>

      <SiteFooter />
    </>
  )
}
